import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_URL } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper functions for cross-subdomain token management
const setTokenAcrossSubdomains = (token) => {
  // Store in localStorage for current domain
  localStorage.setItem('token', token);

  // Store in cookie for subdomain sharing
  const hostname = window.location.hostname;
  if (hostname.includes('localhost')) {
    document.cookie = `auth_token=${token}; domain=.localhost; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax`; // 7 days
  } else if (hostname.includes('condrs.com')) {
    document.cookie = `auth_token=${token}; domain=.condrs.com; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax; Secure`; // 7 days, Secure for HTTPS
  }
};

const setUserAcrossSubdomains = (userData) => {
  const userString = JSON.stringify(userData);
  // Store in localStorage for current domain
  localStorage.setItem('user', userString);

  // Store in cookie for subdomain sharing
  const hostname = window.location.hostname;
  if (hostname.includes('localhost')) {
    document.cookie = `auth_user=${encodeURIComponent(userString)}; domain=.localhost; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax`; // 7 days
  } else if (hostname.includes('condrs.com')) {
    document.cookie = `auth_user=${encodeURIComponent(userString)}; domain=.condrs.com; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax; Secure`; // 7 days, Secure for HTTPS
  }
};

const getTokenFromCookies = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'auth_token') {
      return value;
    }
  }
  return null;
};

const getUserFromCookies = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'auth_user') {
      try {
        return JSON.parse(decodeURIComponent(value));
      } catch (e) {
        return null;
      }
    }
  }
  return null;
};

const clearTokensAcrossSubdomains = () => {
  // Clear localStorage
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
  localStorage.clear();
  sessionStorage.clear();

  // Clear cookies for subdomain sharing
  const hostname = window.location.hostname;
  if (hostname.includes('localhost')) {
    document.cookie = `auth_token=; domain=.localhost; path=/; max-age=0`;
    document.cookie = `auth_user=; domain=.localhost; path=/; max-age=0`;
  } else if (hostname.includes('condrs.com')) {
    document.cookie = `auth_token=; domain=.condrs.com; path=/; max-age=0`;
    document.cookie = `auth_user=; domain=.condrs.com; path=/; max-age=0`;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(null);

  // Try to get token from localStorage first, then cookies
  const getInitialToken = () => {
    const localToken = localStorage.getItem('token');
    if (localToken) return localToken;

    // If no localStorage token, check cookies (for cross-subdomain sharing)
    return getTokenFromCookies();
  };

  const [token, setToken] = useState(getInitialToken());

  // Set up axios defaults and response interceptor
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }

    // Set up axios response interceptor to handle expired tokens
    const interceptor = axios.interceptors.response.use(
      (response) => response, // Pass through successful responses
      (error) => {
        // Check if this is an authentication failure that requires logout
        const isAuthFailure = error.response && (
          // 401 = Unauthorized (authentication required)
          error.response.status === 401 ||
          // 403 with token-related error message (expired/invalid token)
          (error.response.status === 403 &&
           error.response.data?.error &&
           (error.response.data.error.toLowerCase().includes('token') ||
            error.response.data.error.toLowerCase().includes('expired')))
        );

        if (isAuthFailure) {
          // Only auto-logout if we currently have a token (user was logged in)
          if (token) {
            console.log('Token expired or invalid. Logging out...');
            toast.error('Your session has expired. Please log in again.');

            // Clear auth data
            clearTokensAcrossSubdomains();
            setToken(null);
            setUser(null);
            delete axios.defaults.headers.common['Authorization'];

            // Redirect to login
            const hostname = window.location.hostname;
            const isSubdomain = hostname.split('.').length > 2 || (hostname.includes('.') && !hostname.startsWith('localhost'));
            const isLocalhost = hostname.includes('localhost') || hostname.includes('127.0.0.1');
            const timestamp = Date.now();

            if (isSubdomain && isLocalhost) {
              window.location.href = `http://condor.localhost:3000/login?logout=true&t=${timestamp}`;
            } else if (isSubdomain && !isLocalhost) {
              window.location.href = `https://condrs.com/login?logout=true&t=${timestamp}`;
            } else {
              window.location.href = `/login?logout=true&t=${timestamp}`;
            }
          }
        }
        // For other 403 errors (permission denied), just pass through without logging out
        return Promise.reject(error);
      }
    );

    // Cleanup: eject interceptor when component unmounts or token changes
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [token]);

  // Fetch permissions when user changes
  useEffect(() => {
    const fetchPermissions = async () => {
      if (user && user.organizationId && token) {
        try {
          const response = await axios.get(`${API_URL}/settings/permissions`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (response.data && response.data.policy) {
            setPermissions(response.data.policy);
          }
        } catch (error) {
          console.error('Failed to fetch permissions:', error);
          // Set to null on error - will fall back to role-based checks
          setPermissions(null);
        }
      }
    };

    fetchPermissions();
  }, [user, token]);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      console.log('Checking auth, token:', token ? 'exists' : 'none');

      // Check for logout parameter - skip auth check if present
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('logout') === 'true') {
        console.log('Logout parameter detected - skipping auto-login');
        setLoading(false);
        return;
      }

      // First, check for auth parameters in URL (for cross-domain login)
      if (urlParams.get('auth') === 'true' && urlParams.get('token') && urlParams.get('user')) {
        console.log('Found auth parameters in URL');
        const urlToken = urlParams.get('token');
        const urlUser = JSON.parse(decodeURIComponent(urlParams.get('user')));

        // Store the auth data locally and across subdomains
        setTokenAcrossSubdomains(urlToken);
        setUserAcrossSubdomains(urlUser);

        // Set axios header immediately
        axios.defaults.headers.common['Authorization'] = `Bearer ${urlToken}`;

        setToken(urlToken);
        setUser(urlUser);

        // Clean up the URL by removing auth parameters
        const cleanUrl = new URL(window.location);
        cleanUrl.searchParams.delete('auth');
        cleanUrl.searchParams.delete('token');
        cleanUrl.searchParams.delete('user');
        window.history.replaceState({}, document.title, cleanUrl.toString());

        console.log('Auth data saved from URL parameters');
        setLoading(false);
        return;
      }

      // Check localStorage for existing auth, fallback to cookies
      if (token) {
        // Get user data from localStorage first, then cookies
        let storedUser = localStorage.getItem('user');
        let userData = null;

        if (storedUser) {
          try {
            userData = JSON.parse(storedUser);
            console.log('Found stored user data in localStorage:', userData);
          } catch (error) {
            console.error('Error parsing stored user data from localStorage:', error);
          }
        }

        // If no localStorage user data but we have a token (from cookies), try cookies for user data
        if (!userData) {
          userData = getUserFromCookies();
          if (userData) {
            console.log('Found user data in cookies:', userData);
            // Also store in localStorage for current domain
            localStorage.setItem('user', JSON.stringify(userData));
            localStorage.setItem('token', token);
          }
        }

        if (userData) {
          // Ensure axios header is set immediately
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          setUser(userData);
        } else {
          console.log('No user data in localStorage or cookies');
          logout();
        }
      } else {
        console.log('No token found in localStorage or cookies');
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (username, password, organizationId = null) => {
    try {
      // If organizationId is provided, use the select-account endpoint
      const endpoint = organizationId ? `${API_URL}/auth/select-account` : `${API_URL}/auth/login`;
      const payload = organizationId
        ? { username, password, organizationId }
        : { username, password };

      const response = await axios.post(endpoint, payload);

      console.log('Full backend response:', response.data);

      // Check if backend returned multiple accounts
      if (response.data.multipleAccounts) {
        console.log('🔐 Multiple accounts detected:', response.data.accounts);
        // Return the accounts list to the login component
        return {
          success: true,
          multipleAccounts: true,
          accounts: response.data.accounts,
          message: response.data.message
        };
      }

      // Normal login flow - single account
      const { token: newToken, user: userData, redirectUrl } = response.data;
      console.log('Extracted redirectUrl:', redirectUrl);

      setTokenAcrossSubdomains(newToken);
      setUserAcrossSubdomains(userData);

      // Set axios header immediately
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      setToken(newToken);
      setUser(userData);

      toast.success('Login successful!');

      // Use the redirectUrl from backend if provided
      if (redirectUrl) {
        console.log('🔗 AUTH CONTEXT: Redirecting to backend-provided URL:', redirectUrl);

        // Check if we're redirecting to a different domain/subdomain
        const currentHost = window.location.hostname;
        const redirectUrlObj = new URL(redirectUrl);
        const redirectHost = redirectUrlObj.hostname;

        // If redirecting to a different domain/subdomain, append auth parameters
        if (currentHost !== redirectHost) {
          console.log('🔗 AUTH CONTEXT: Cross-domain redirect detected');
          console.log('🔗 AUTH CONTEXT: Current host:', currentHost);
          console.log('🔗 AUTH CONTEXT: Redirect host:', redirectHost);

          // Append auth parameters to URL for cross-domain authentication
          redirectUrlObj.searchParams.set('auth', 'true');
          redirectUrlObj.searchParams.set('token', newToken);
          redirectUrlObj.searchParams.set('user', encodeURIComponent(JSON.stringify(userData)));

          const finalUrl = redirectUrlObj.toString();
          console.log('🔗 AUTH CONTEXT: Redirect URL with auth params:', finalUrl);
          window.location.href = finalUrl;
        } else {
          // Same domain redirect - just use the URL as-is
          console.log('🔗 AUTH CONTEXT: Same-domain redirect - using URL as-is');
          const cleanUrl = redirectUrl.replace('//', '/').replace('http:/', 'http://').replace('https:/', 'https://');
          window.location.href = cleanUrl;
        }
      } else {
        console.log('No redirectUrl provided by backend');
      }

      return { success: true, user: userData, token: newToken };
    } catch (error) {
      // Handle network errors specifically
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        const networkMessage = 'Network connection error. Please check your internet connection or try a different network.';
        toast.error(networkMessage);
        return { success: false, error: networkMessage, isNetworkError: true };
      }

      // Handle other errors (like invalid credentials)
      const message = error.response?.data?.message || error.response?.data?.error || 'Login failed';
      toast.error(message);
      return { success: false, error: message, isNetworkError: false };
    }
  };

  const logout = () => {
    console.log('Logging out user...');

    // Clear auth state immediately
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];

    // Clear cookies and storage
    clearTokensAcrossSubdomains();

    toast.success('Logged out successfully');

    // Redirect to the main domain login page after logout
    const hostname = window.location.hostname;
    const isSubdomain = hostname.split('.').length > 2 || (hostname.includes('.') && !hostname.startsWith('localhost'));
    const isLocalhost = hostname.includes('localhost') || hostname.includes('127.0.0.1');

    // Add cache-busting and logout parameter to prevent auto-login
    const timestamp = Date.now();

    // Use setTimeout to ensure cookies are cleared before redirect
    setTimeout(() => {
      if (isSubdomain && isLocalhost) {
        // Redirect to main condor.localhost for local development
        window.location.href = `http://condor.localhost:3000/login?logout=true&t=${timestamp}`;
      } else if (isSubdomain && !isLocalhost) {
        // Redirect to main condrs.com for production
        window.location.href = `https://condrs.com/login?logout=true&t=${timestamp}`;
      } else {
        // Already on main domain, just go to login
        window.location.href = `/login?logout=true&t=${timestamp}`;
      }
    }, 100); // 100ms delay to ensure cookies are cleared
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  // Helper function to check if user has a specific permission
  const hasPermission = (feature, action) => {
    if (!user) return false;

    // SUPER_ADMIN always has all permissions
    if (user.role === 'SUPER_ADMIN') return true;

    // BUSINESS_ADMIN always has all permissions (not configurable)
    if (user.role === 'BUSINESS_ADMIN') return true;

    // If no permissions loaded yet, allow access (will be enforced by backend)
    // This prevents blocking users while permissions are loading
    if (!permissions) return true;

    // Determine role bucket for permission lookup
    let roleBucket = 'EMPLOYEE';
    if (user.role === 'EMPLOYEE' && user.accessLevel === 'MANAGER') {
      roleBucket = 'MANAGER';
    }

    // Check permission in policy
    return !!permissions?.[roleBucket]?.[feature]?.[action];
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    permissions,
    hasPermission,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'BUSINESS_ADMIN' || user?.role === 'SUPER_ADMIN',
    isAdminOrManager: user?.role === 'BUSINESS_ADMIN' || user?.role === 'SUPER_ADMIN' || (user?.role === 'EMPLOYEE' && user?.accessLevel === 'MANAGER'),
    isSuperAdmin: user?.role === 'SUPER_ADMIN',
    isBusinessAdmin: user?.role === 'BUSINESS_ADMIN',
    isEmployee: user?.role === 'EMPLOYEE',
    isManager: user?.role === 'EMPLOYEE' && user?.accessLevel === 'MANAGER',
    // Permission checks using the permissions system
    canManageInventory: hasPermission('inventoryItems', 'view') || hasPermission('inventoryItems', 'create'),
    canViewSettings: hasPermission('systemSettings', 'manageLocations') || user?.role === 'BUSINESS_ADMIN' || user?.role === 'SUPER_ADMIN',
    canManageUsers: hasPermission('userManagement', 'viewUsers') || user?.role === 'BUSINESS_ADMIN' || user?.role === 'SUPER_ADMIN',
    canViewEmployees: hasPermission('userManagement', 'viewUsers') || user?.role === 'BUSINESS_ADMIN' || user?.role === 'SUPER_ADMIN',
    canProcessReturns: hasPermission('reservations', 'processReturn') || user?.role === 'BUSINESS_ADMIN' || user?.role === 'SUPER_ADMIN'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};